import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dossiersAPI, billingAPI, orgsAPI, tendersAPI } from '../api/api';
import { toast } from '../hooks/use-toast';
import AppLayout from '../components/AppLayout';
import { useCountUp } from '../hooks/useCountUp';

// ─── Helpers ────────────────────────────────────────────────────────────────
const formatRelDate = (date) => {
  const now = new Date();
  const d = new Date(date);
  const diff = Math.floor((now - d) / 86400000);
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return 'Hier';
  if (diff < 7) return `Il y a ${diff} jours`;
  if (diff < 30) return `Il y a ${Math.floor(diff / 7)} sem.`;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

// ─── Inline org creation ─────────────────────────────────────────────────────
const CreateOrgInline = ({ onCreated }) => {
  const [name, setName] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const toSlug = (str) =>
    str.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').substring(0, 50);
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      await orgsAPI.create({ name: name.trim(), slug: toSlug(name) || `org-${Date.now()}` });
      onCreated();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={handleCreate} style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
      <input
        style={{
          background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8,
          padding: '9px 13px', color: 'var(--white)', fontSize: 13, outline: 'none',
          width: 240, marginRight: 8, fontFamily: 'inherit', transition: 'border-color 0.15s',
        }}
        placeholder="Nom de l'organisation"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
      />
      <button
        type="submit"
        disabled={loading}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '7px 13px', borderRadius: 7, fontSize: 12, fontWeight: 600,
          cursor: 'pointer', border: 'none', background: 'var(--vio)', color: 'white',
          opacity: loading ? 0.6 : 1, fontFamily: 'inherit',
        }}
      >
        {loading ? '…' : 'Créer'}
      </button>
    </form>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const Dashboard = () => {
  const { user, currentOrg, orgLoadError, orgLoading, logout, loading: authLoading, refreshOrg } = useAuth();
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [stats, setStats] = useState({ dossiers: 0, aiParses: 0 });
  const [tenderAnalytics, setTenderAnalytics] = useState(null);

  // ── Auth guard ──
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // ── Fetch data ──
  const fetchData = useCallback(async () => {
    setDataLoading(true);
    try {
      const [data, usage, analytics] = await Promise.all([
        dossiersAPI.getAll(),
        billingAPI.getUsage().catch(() => ({})),
        tendersAPI.analytics().catch(() => null),
      ]);
      setDossiers(data);
      setStats({
        dossiers: data.length,
        aiParses: usage.ai_parse_count || 0,
      });
      setTenderAnalytics(analytics);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les dossiers',
        variant: 'destructive',
      });
    } finally {
      setDataLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentOrg) {
      fetchData();
    } else if (!authLoading) {
      setDataLoading(false);
    }
  }, [currentOrg, authLoading, fetchData]);

  // ── Coming soon helper ──
  const comingSoon = (label = '') => {
    toast({
      title: '🚀 Bientôt disponible',
      description: `${label ? label + ' — ' : ''}Cette fonctionnalité sera disponible prochainement.`,
      duration: 3000,
    });
  };

  // ── Computed ──
  const recentActivity = dossiers
    .slice()
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 5);

  // ── Animated counters for KPI tiles ──
  const dossiersCount = useCountUp(stats.dossiers);
  const aiParsesCount = useCountUp(stats.aiParses);

  // ── Loading spinner ──
  if (authLoading || orgLoading) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{`@keyframes sd-spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ width: 40, height: 40, borderRadius: '50%', border: '2.5px solid var(--border)', borderTopColor: 'var(--vio)', animation: 'sd-spin 1s linear infinite' }} />
      </div>
    );
  }

  // ── Dashboard fusionné : le Cockpit BM est désormais le tableau de bord.
  // /dashboard n'est plus qu'un aiguillage — les utilisateurs rattachés à une
  // organisation sont redirigés vers /cockpit ; les autres gardent le
  // garde-fou de création d'organisation ci-dessous.
  if (currentOrg) {
    return <Navigate to="/cockpit" replace />;
  }

  // ── No org → creation form or error retry ──
  if (!currentOrg && !authLoading && !orgLoading) {
    // API failure case — show retry button, not create org form
    if (orgLoadError) {
      return (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'Inter, system-ui, sans-serif' }}>
          <div style={{ textAlign: 'center', maxWidth: 420 }}>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--white)', marginBottom: 8 }}>Erreur de chargement</h2>
            <p style={{ fontSize: 13, color: 'var(--muted2)', marginBottom: 24, lineHeight: 1.6 }}>
              Impossible de charger votre organisation. Vérifiez votre connexion et réessayez.
            </p>
            <button
              onClick={() => refreshOrg()}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                cursor: 'pointer', border: 'none', background: 'var(--vio)', color: 'white',
                fontFamily: 'inherit',
              }}
            >
              Réessayer
            </button>
            <button
              onClick={async () => { await logout(); navigate('/'); }}
              style={{ display: 'block', margin: '12px auto 0', fontSize: 12, color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit' }}
            >
              Se déconnecter
            </button>
          </div>
        </div>
      );
    }

    // Genuinely no org — show creation form
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'Inter, system-ui, sans-serif' }}>
        <div style={{ textAlign: 'center', maxWidth: 420 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--white)', marginBottom: 8 }}>Aucune organisation</h2>
          <p style={{ fontSize: 13, color: 'var(--muted2)', marginBottom: 24, lineHeight: 1.6 }}>
            Votre compte n'est rattaché à aucune organisation.
            <br />Créez-en une pour commencer à utiliser Skilldeck.
          </p>
          <CreateOrgInline onCreated={() => window.location.reload()} />
          <button
            onClick={async () => { await logout(); navigate('/'); }}
            style={{ marginTop: 16, fontSize: 12, color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit' }}
          >
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  // ── Main render ──
  return (
    <AppLayout
      title="Tableau de bord"
      dossiersCount={stats.dossiers}
    >
      {/* ── Stats Row ── */}
      <div className="sd-stats-row sd-stagger">
            {dataLoading ? (
              [1, 2, 3, 4].map((i) => <div key={i} className="sd-stat-skeleton" />)
            ) : (
              <>
                <div className="sd-stat-card">
                  <div className="sd-stat-label">Total dossiers</div>
                  <div className="sd-stat-value">{dossiersCount}</div>
                  <span className="sd-stat-delta up">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M5 8V2M2 5l3-3 3 3"/></svg>
                    actifs
                  </span>
                  <div className="sd-stat-icon" style={{ background: 'rgba(108,71,255,0.1)', color: 'var(--vio2)' }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <rect x="2" y="4" width="10" height="9" rx="2"/>
                      <path d="M5 4V3a2 2 0 014 0v1M4 7h6M4 9.5h4"/>
                    </svg>
                  </div>
                </div>

                <div className="sd-stat-card">
                  <div className="sd-stat-label">Envoyés ce mois</div>
                  <div className="sd-stat-value">{aiParsesCount}</div>
                  <span className="sd-stat-delta up">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M5 8V2M2 5l3-3 3 3"/></svg>
                    analyses IA
                  </span>
                  <div className="sd-stat-icon" style={{ background: 'rgba(59,130,246,0.1)', color: '#60A5FA' }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <path d="M2.5 13.5 13.5 2.5M13.5 2.5H8M13.5 2.5V8"/>
                    </svg>
                  </div>
                </div>

                <div className="sd-stat-card">
                  <div className="sd-stat-label">AOs actifs</div>
                  <div className="sd-stat-value">0</div>
                  <span className="sd-stat-delta neutral">bientôt disponible</span>
                  <div className="sd-stat-icon" style={{ background: 'rgba(167,139,250,0.1)', color: 'var(--vio2)' }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M8 1.5 9.5 5.8 14 8 9.5 10.2 8 14.5 6.5 10.2 2 8 6.5 5.8Z"/>
                    </svg>
                  </div>
                </div>

                <div className="sd-stat-card">
                  <div className="sd-stat-label">Taux placement</div>
                  <div className="sd-stat-value">—%</div>
                  <span className="sd-stat-delta neutral">bientôt disponible</span>
                  <div className="sd-stat-icon" style={{ background: 'rgba(34,197,94,0.1)', color: '#4ADE80' }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 11 6 7l2.5 3 4.5-5.5M2 14h12"/>
                    </svg>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* ── Dashboard 2-col Grid ── */}
          <div className="sd-dash-grid">

            <div className="sd-dash-left">
              {/* Upcoming AO deadlines */}
              <div className="sd-dash-section">
                <div className="sd-dash-section-head">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" style={{ color: 'var(--vio2)' }}>
                      <rect x="1.5" y="4.5" width="11" height="8" rx="1.5"/>
                      <path d="M4.5 4.5V3.5a2 2 0 014 0v1M1.5 8.5h11"/>
                    </svg>
                    <span className="sd-dash-section-title">AO — échéances à venir</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className="sd-dash-section-count">{tenderAnalytics?.upcoming_deadlines?.length || 0}</span>
                    <button className="sd-dash-section-link" onClick={() => navigate('/tenders')}>
                      Voir tous →
                    </button>
                  </div>
                </div>
                <div className="sd-dash-section-body">
                  {!tenderAnalytics ? (
                    <div style={{ padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {[1, 2].map(i => <div key={i} className="sd-skeleton" style={{ height: 14, width: i === 2 ? '70%' : '85%' }} />)}
                    </div>
                  ) : (tenderAnalytics.upcoming_deadlines?.length || 0) === 0 ? (
                    <div className="sd-empty-state">
                      <div className="sd-empty-icon">📋</div>
                      <div className="sd-empty-title">Aucune échéance dans les 21 jours</div>
                      <div className="sd-empty-sub">{tenderAnalytics.total > 0 ? `${tenderAnalytics.total} AO au total` : 'Importez votre premier AO pour démarrer.'}</div>
                    </div>
                  ) : (
                    tenderAnalytics.upcoming_deadlines.map(t => {
                      const days = t.days_remaining;
                      const color = days < 0 ? '#F87171' : days <= 3 ? '#FB923C' : days <= 7 ? '#FACC15' : 'var(--vio2)';
                      const label = days < 0 ? `Dépassée ${Math.abs(days)}j` : days === 0 ? "Aujourd'hui" : `Dans ${days}j`;
                      return (
                        <div
                          key={t.id}
                          onClick={() => navigate(`/tenders/${t.id}`)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 11,
                            padding: '10px 18px',
                            borderBottom: '1px solid var(--border)',
                            cursor: 'pointer',
                          }}
                        >
                          <div style={{
                            fontSize: 10, fontWeight: 700,
                            color, background: `${color}1a`,
                            padding: '3px 8px', borderRadius: 6,
                            border: `1px solid ${color}44`,
                            whiteSpace: 'nowrap', flexShrink: 0, minWidth: 60, textAlign: 'center',
                          }}>{label}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {t.title}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--muted2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {t.client || '—'}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Win/Loss summary */}
              {tenderAnalytics && tenderAnalytics.total > 0 && (
                <div className="sd-dash-section">
                  <div className="sd-dash-section-head">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#4ADE80' }}>
                        <path d="M2 11 6 7l2.5 3 4.5-5.5"/><path d="M2 13.5h12"/>
                      </svg>
                      <span className="sd-dash-section-title">Performance commerciale</span>
                    </div>
                  </div>
                  <div style={{ padding: '16px 18px', display: 'flex', justifyContent: 'space-around', textAlign: 'center', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: '#4ADE80', lineHeight: 1 }}>{tenderAnalytics.wins}</div>
                      <div style={{ fontSize: 10.5, color: 'var(--muted2)', marginTop: 4, fontWeight: 500 }}>Gagnés</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: '#F87171', lineHeight: 1 }}>{tenderAnalytics.losses}</div>
                      <div style={{ fontSize: 10.5, color: 'var(--muted2)', marginTop: 4, fontWeight: 500 }}>Perdus</div>
                    </div>
                    <div>
                      <div style={{
                        fontSize: 22, fontWeight: 700, lineHeight: 1,
                        color: tenderAnalytics.win_rate == null ? 'var(--muted)' : tenderAnalytics.win_rate >= 50 ? '#4ADE80' : '#FB923C',
                      }}>
                        {tenderAnalytics.win_rate == null ? '—' : `${tenderAnalytics.win_rate}%`}
                      </div>
                      <div style={{ fontSize: 10.5, color: 'var(--muted2)', marginTop: 4, fontWeight: 500 }}>Taux de réussite</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="sd-dash-right">
              {/* Activité récente */}
              <div className="sd-dash-section">
                <div className="sd-dash-section-head">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" style={{ color: 'var(--muted2)' }}>
                      <circle cx="7" cy="7" r="5.5"/><path d="M7 4v3l2 1.5"/>
                    </svg>
                    <span className="sd-dash-section-title">Activité récente</span>
                  </div>
                </div>
                <div className="sd-dash-section-body">
                  {dataLoading ? (
                    <div style={{ padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="sd-skeleton" style={{ height: 12, width: i === 2 ? '60%' : '80%' }} />
                      ))}
                    </div>
                  ) : recentActivity.length === 0 ? (
                    <div className="sd-empty-state">
                      <div className="sd-empty-icon">📄</div>
                      <div className="sd-empty-title">Aucune activité récente</div>
                      <div className="sd-empty-sub">Créez votre premier dossier pour démarrer.</div>
                    </div>
                  ) : (
                    recentActivity.map((d) => (
                      <div key={d.id} className="sd-activity-item">
                        <div className="sd-activity-dot" style={{ background: 'var(--vio2)' }} />
                        <div className="sd-activity-text">
                          <div className="sd-activity-label">
                            Dossier <strong>{d.consultant_name || d.name}</strong> créé
                          </div>
                          <div className="sd-activity-time">{formatRelDate(d.created_at)}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Accès rapide */}
              <div className="sd-quick">
                <div className="sd-quick-title">Accès rapide</div>
                <div className="sd-quick-btns">
                  <button className="sd-quick-btn primary" onClick={() => navigate('/dossier/new')}>
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M6.5 1v11M1 6.5h11"/>
                    </svg>
                    Nouveau dossier de compétences
                  </button>
                  <button className="sd-quick-btn" onClick={() => comingSoon('Matching IA')}>
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--vio2)' }}>
                      <path d="M6.5 1 8 5 12.5 6.5 8 8 6.5 12 5 8 .5 6.5 5 5z"/>
                    </svg>
                    Lancer un matching IA
                  </button>
                  <button className="sd-quick-btn" onClick={() => navigate('/library')}>
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <rect x="1" y="2" width="11" height="9" rx="1.5"/><path d="M4 6h5M4 8.5h3"/>
                    </svg>
                    Bibliothèque de dossiers
                  </button>
                </div>
              </div>
            </div>
          </div>

    </AppLayout>
  );
};

export default Dashboard;
